/**
 * cleanupHistory — user-configurable retention cap for the per-category
 * cleanup history stored under `cdi:cleanup_history:{categoryId}` in
 * localStorage. The cap is set from Settings → Cleanup History and
 * defaults to 10 (the historic hard-coded value) so existing users see
 * no behaviour change until they change it.
 *
 * Values persisted verbatim so a downgrade never re-shows stale runs:
 * on the next write, `.slice(-cap)` trims the tail. Pure client-side —
 * no backend, no schema.
 */
export const CLEANUP_HISTORY_CAP_KEY = "cdi:cleanup_history_cap";
export const CLEANUP_HISTORY_CAP_DEFAULT = 10;
export const CLEANUP_HISTORY_CAP_CHOICES = [5, 10, 20, 50, 100];

/**
 * Read the user-selected cap from localStorage. Falls back to the
 * default when the key is missing, the value is unparseable, or it
 * isn't one of the offered choices (guards against tampering + future
 * value list edits).
 */
export function getCleanupHistoryCap() {
  if (typeof window === "undefined") return CLEANUP_HISTORY_CAP_DEFAULT;
  try {
    const raw = window.localStorage.getItem(CLEANUP_HISTORY_CAP_KEY);
    if (raw === null) return CLEANUP_HISTORY_CAP_DEFAULT;
    const n = parseInt(raw, 10);
    return CLEANUP_HISTORY_CAP_CHOICES.includes(n)
      ? n
      : CLEANUP_HISTORY_CAP_DEFAULT;
  } catch {
    return CLEANUP_HISTORY_CAP_DEFAULT;
  }
}

/** Persist the selected cap. Silently ignores private-mode quota errors. */
export function setCleanupHistoryCap(n) {
  if (typeof window === "undefined") return;
  if (!CLEANUP_HISTORY_CAP_CHOICES.includes(n)) return;
  try {
    window.localStorage.setItem(CLEANUP_HISTORY_CAP_KEY, String(n));
  } catch { /* noop */ }
}
